import React from "react";
import { Card, CardContent } from "@shared/ui/card";
import { MetricCardProps } from "../../types";
import { iconColors, valueColors, variantStyles } from "../../utils";

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon: Icon,
  subtitle,
  variant = "white",
  iconColor,
  size = "md",
  position,
  onClick,
}) => {
  const formatValue = (value: string | number) => {
    return typeof value === "number" ? value.toLocaleString() : value;
  };

  const renderIcon = () => {
    if (!Icon) return null;
    return (
      <div className={` rounded ${size === "sm" ? "p-1.5" : "p-2"}`}>
        <Icon
          className={`${size === "sm" ? "h-5 w-5" : "h-7 w-7"} ${
            iconColor || "text-primary"
          }`}
        />
      </div>
    );
  };

  const renderTopLayout = () => (
    <>
      {Icon && (
        <div className="flex items-center justify-between mb-2">
          {renderIcon()}
        </div>
      )}
      <p className="text-sm font-medium">{title}</p>
      <div className="space-y-1">
        <p
          className={`${size === "sm" ? "text-lg" : "text-2xl"} font-bold ${
            valueColors[variant]
          }`}
        >
          {formatValue(value)}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </>
  );

  const renderSideLayout = () => (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {Icon && renderIcon()}
        <p className="text-sm font-medium">{title}</p>
      </div>
      <p
        className={`${size === "sm" ? "text-lg" : "text-2xl"} font-bold ${
          valueColors[variant]
        }`}
      >
        {formatValue(value)}
      </p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );

  const renderRightLayout = () => (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p
          className={`${size === "sm" ? "text-lg" : "text-2xl"} font-bold ${
            valueColors[variant]
          }`}
        >
          {formatValue(value)}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {Icon && renderIcon()}
    </div>
  );

  const cardContent = (
    <Card
      className={`${variantStyles[variant]} ${
        onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""
      }`}
    >
      <CardContent className="p-2">
        {position === "side"
          ? renderSideLayout()
          : position === "right"
          ? renderRightLayout()
          : renderTopLayout()}
      </CardContent>
    </Card>
  );

  return onClick ? (
    <div onClick={onClick} role="button" tabIndex={0}>
      {cardContent}
    </div>
  ) : (
    cardContent
  );
};
